import PropTypes from 'prop-types';
import React from 'react';

const GT_SCRIPT_CB_NAME = '__googleTranslateElementInitLivableStreets';

/** Languages shown in the custom picker (subset of Google Website Translator codes). */
const LANGUAGE_CHOICES = [
  { value: '', native: 'English', flag: 'en' },
  { value: 'zh-TW', native: '繁體中文', flag: 'zh-TW' },
  { value: 'ja', native: '日本語', flag: 'ja' },
  { value: 'ko', native: '한국어', flag: 'ko' },
  { value: 'ru', native: 'Русский', flag: 'ru' },
  { value: 'es', native: 'Español', flag: 'es' },
  { value: 'tl', native: 'Filipino', flag: 'tl' },
  { value: 'zh-CN', native: '简体中文', flag: 'zh-CN' },
  { value: 'fr', native: 'Français', flag: 'fr' },
  { value: 'de', native: 'Deutsch', flag: 'de' },
  { value: 'it', native: 'Italiano', flag: 'it' },
  { value: 'pt', native: 'Português', flag: 'pt' },
  { value: 'ar', native: 'العربية', flag: 'ar' },
  { value: 'vi', native: 'Tiếng Việt', flag: 'vi' },
  { value: 'hi', native: 'हिन्दी', flag: 'hi' },
];

const INCLUDED = LANGUAGE_CHOICES.map((l) => l.value).filter(Boolean).join(',');

/** Google’s iframe menu labels (English) for each Website Translator language code. */
const VALUE_TO_GOOGLE_MENU_LABEL = {
  'zh-TW': 'Chinese (Traditional)',
  'zh-CN': 'Chinese (Simplified)',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian',
  es: 'Spanish',
  tl: 'Filipino',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese (Brazil)',
  ar: 'Arabic',
  vi: 'Vietnamese',
  hi: 'Hindi',
};

function findTranslateCombo(){
  return (
    document.querySelector('#google_translate_element select.goog-te-combo') ||
    document.querySelector('#google_translate_element .goog-te-combo') ||
    document.querySelector('select.goog-te-combo')
  );
}

function findTranslateMenuIframe(){
  const iframes = document.querySelectorAll('iframe');
  for(let i = 0; i < iframes.length; i += 1){
    const frame = iframes[i];
    try{
      const doc = frame.contentDocument;
      if(doc && doc.querySelector('.text')){
        return frame;
      }
    } catch (err){
      // Cross-origin or inaccessible frame — skip.
    }
  }
  return null;
}

function findTranslateMenuTrigger(){
  return (
    document.querySelector('#google_translate_element .goog-te-gadget-simple a[aria-haspopup]') ||
    document.querySelector('#google_translate_element a[aria-haspopup]') ||
    document.querySelector('#google_translate_element .goog-te-gadget-simple a')
  );
}

function isPageTranslated(){
  const gadgetLabel = document
    .querySelector('#google_translate_element .goog-te-gadget-simple a span')
    ?.textContent
    ?.trim();
  if(gadgetLabel && gadgetLabel !== 'Select Language'){
    return true;
  }
  return document.cookie.indexOf('googtrans=/en/') !== -1;
}

function clearGoogTransCookie(){
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `googtrans=;expires=${expires};path=/`;
  const host = window.location.hostname;
  if(host){
    document.cookie = `googtrans=;expires=${expires};path=/;domain=${host}`;
    document.cookie = `googtrans=;expires=${expires};path=/;domain=.${host}`;
  }
}

function clickGoogleRestoreButton(){
  const iframe =
    document.querySelector('iframe.goog-te-banner-frame') ||
    document.querySelector('iframe.skiptranslate');
  if(!iframe) return false;
  let doc;
  try{
    doc = iframe.contentDocument || iframe.contentWindow.document;
  } catch (err){
    return false;
  }
  if(!doc) return false;
  const buttons = doc.getElementsByTagName('button');
  for(let i = 0; i < buttons.length; i += 1){
    if(buttons[i].id && buttons[i].id.indexOf('restore') >= 0){
      buttons[i].click();
      return true;
    }
  }
  return false;
}

function flagSrc(flagCode) {
  // gtranslate.net uses zh-TW / zh-CN casing; other codes are lowercase (en, es, ja, …).
  if(flagCode === 'zh-TW' || flagCode === 'zh-CN'){
    return `https://cdn.gtranslate.net/flags/32/${flagCode}.png`;
  }
  return `https://cdn.gtranslate.net/flags/32/${(flagCode || 'en').toLowerCase()}.png`;
}

const GT_BANNER_HEIGHT_VAR = '--ls-gt-banner-height';
const GT_BANNER_ACTIVE_CLASS = 'ls-google-translate-banner-active';

function findGoogleTranslateBannerIframe(){
  return (
    document.querySelector('iframe.goog-te-banner-frame') ||
    document.querySelector('body > .skiptranslate iframe.skiptranslate') ||
    document.querySelector('iframe.skiptranslate.VIpgJd-ZVi9od-ORHb-OEVmcd')
  );
}

function measureGoogleTranslateBannerHeight(){
  const iframe = findGoogleTranslateBannerIframe();
  if(!iframe){
    return 0;
  }
  const style = window.getComputedStyle(iframe);
  if(style.display === 'none' || style.visibility === 'hidden'){
    return 0;
  }
  const rect = iframe.getBoundingClientRect();
  if(rect.height <= 0 || rect.top > 2){
    return 0;
  }
  return Math.ceil(rect.height);
}

function applyGoogleTranslateBannerOffset(forcedHeight){
  const height =
    typeof forcedHeight === 'number' ? forcedHeight : measureGoogleTranslateBannerHeight();
  document.documentElement.style.setProperty(GT_BANNER_HEIGHT_VAR, `${height}px`);
  document.body.classList.toggle(GT_BANNER_ACTIVE_CLASS, height > 0);
}

function startGoogleTranslateBannerOffset(){
  const update = () => applyGoogleTranslateBannerOffset();
  let resizeObserver = null;

  const attachResizeObserver = () => {
    const iframe = findGoogleTranslateBannerIframe();
    if(!iframe || resizeObserver){
      return;
    }
    resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(iframe);
  };

  const mutationObserver = new MutationObserver(() => {
    update();
    attachResizeObserver();
  });
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  const pollId = window.setInterval(() => {
    update();
    attachResizeObserver();
  }, 200);
  window.setTimeout(() => window.clearInterval(pollId), 20000);

  window.addEventListener('resize', update);
  update();
  attachResizeObserver();

  return () => {
    mutationObserver.disconnect();
    if(resizeObserver){
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    window.clearInterval(pollId);
    window.removeEventListener('resize', update);
    applyGoogleTranslateBannerOffset(0);
  };
}

function detachTranslateEngineFromTabOrder(engine){
  if(!engine) return;
  engine.setAttribute('aria-hidden', 'true');
  if('inert' in HTMLElement.prototype){
    engine.inert = true;
  }
  engine.querySelectorAll('a, button, select, input, textarea, [tabindex]').forEach((el) => {
    el.setAttribute('tabindex', '-1');
  });
}

function triggerHtmlChange(element){
  if(!element) return;
  if(typeof element.dispatchEvent === 'function'){
    try{
      element.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    } catch (err){
      // Fall through to legacy initEvent.
    }
    if(typeof document.createEvent === 'function'){
      const event = document.createEvent('HTMLEvents');
      event.initEvent('change', true, true);
      element.dispatchEvent(event);
    }
  }
}

export default class GoogleTranslate extends React.Component{
  static propTypes = {
    /** `header` = LADOT-style icon + dropdown; legacy brand-bar select (unused after global nav). */
    variant: PropTypes.oneOf(['header', 'legacy']),
  };

  static defaultProps = {
    variant: 'legacy',
  };

  state = {
    menuOpen: false,
    engineReady: false,
    items: [...LANGUAGE_CHOICES],
    selectedValue: '',
  };

  constructor(props){
    super(props);
    this._rootRef = React.createRef();
    this._engineRef = React.createRef();
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onComboChange = this._onComboChange.bind(this);
    this._onEngineReady = this._onEngineReady.bind(this);
    this.googleTranslateElementInit = this.googleTranslateElementInit.bind(this);
  }

  _toggleMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.clearTimeout(this._menuCloseTimer);
    this.setState((s) => ({ menuOpen: !s.menuOpen }), () => {
      if(this.state.menuOpen){
        this._scheduleClampMenuToViewport();
      } else {
        this._resetMenuViewportClamp();
      }
    });
  };

  _scheduleClampMenuToViewport(){
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this._clampMenuToViewport());
    });
  }

  _resetMenuViewportClamp(){
    const menu = this._rootRef.current?.querySelector('.gt_option');
    if(menu){
      menu.style.transform = '';
    }
  }

  _clampMenuToViewport(){
    const menu = this._rootRef.current?.querySelector('.gt_option');
    if(!menu){
      return;
    }
    menu.style.transform = '';
    const padding = 8;
    const rect = menu.getBoundingClientRect();
    let shiftX = 0;
    if(rect.right > window.innerWidth - padding){
      shiftX -= rect.right - (window.innerWidth - padding);
    }
    if(rect.left < padding){
      shiftX += padding - rect.left;
    }
    if(shiftX !== 0){
      menu.style.transform = `translateX(${shiftX}px)`;
    }
  }

  _onViewportChange = () => {
    if(this.state.menuOpen){
      this._clampMenuToViewport();
    }
  };

  _onSelectedBtnKeyDown = (e) => {
    if(e.key === ' ' || e.key === 'Spacebar'){
      e.preventDefault();
      e.stopPropagation();
      this._toggleMenu(e);
    }
  };

  _detachEngineFromTabOrder = () => {
    const engine = this._engineRef.current || document.getElementById('google_translate_element');
    detachTranslateEngineFromTabOrder(engine);
  };

  _watchEngineFocusables = () => {
    const engine = this._engineRef.current;
    if(!engine || this._engineMutationObserver){
      return;
    }
    this._engineMutationObserver = new MutationObserver(() => {
      this._detachEngineFromTabOrder();
    });
    this._engineMutationObserver.observe(engine, {childList: true, subtree: true});
    this._detachEngineFromTabOrder();
  };

  componentDidMount(){
    this._ensureTranslateScript();
    this._stopBannerOffset = startGoogleTranslateBannerOffset();
    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onKeyDown, true);
    window.addEventListener('resize', this._onViewportChange);
    if(this.props.variant === 'header'){
      this._watchEngineFocusables();
    }
  }

  componentDidUpdate(_prevProps, prevState){
    if(this.props.variant !== 'header'){
      return;
    }
    if(this.state.menuOpen && !prevState.menuOpen){
      this._scheduleClampMenuToViewport();
    }
    if(!this.state.menuOpen && prevState.menuOpen){
      this._resetMenuViewportClamp();
    }
  }

  componentWillUnmount(){
    if(this._stopBannerOffset){
      this._stopBannerOffset();
      this._stopBannerOffset = null;
    }
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onKeyDown, true);
    window.removeEventListener('resize', this._onViewportChange);
    window.clearTimeout(this._menuCloseTimer);
    this._menuCloseTimer = null;
    if(this._comboPollId){
      window.clearInterval(this._comboPollId);
      this._comboPollId = null;
    }
    if(this._combo && this._onComboChange){
      this._combo.removeEventListener('change', this._onComboChange);
    }
    if(this._engineMutationObserver){
      this._engineMutationObserver.disconnect();
      this._engineMutationObserver = null;
    }
  }

  _onKeyDown = (e) => {
    if(e.key === 'Escape' && this.state.menuOpen){
      this.setState({ menuOpen: false });
    }
  };

  _onDocPointerDown(e){
    if(!this.state.menuOpen) return;
    const root = this._rootRef.current;
    if(root && root.contains(e.target)){
      return;
    }
    // Defer so the opening click on the trigger is not treated as an outside close.
    window.setTimeout(() => {
      if(!this.state.menuOpen) return;
      const currentRoot = this._rootRef.current;
      if(currentRoot && e.target instanceof Node && currentRoot.contains(e.target)){
        return;
      }
      this.setState({ menuOpen: false });
    }, 0);
  }

  _ensureTranslateScript(){
    if(window.google && window.google.translate && window.google.translate.TranslateElement){
      this.googleTranslateElementInit();
      return;
    }
    window.__lsGoogleTranslateReadyCallbacks = window.__lsGoogleTranslateReadyCallbacks || [];
    window.__lsGoogleTranslateReadyCallbacks.push(() => this.googleTranslateElementInit());

    if(document.querySelector('script[data-ls-google-translate]')){
      return;
    }

    window[GT_SCRIPT_CB_NAME] = function(){
      const cbs = window.__lsGoogleTranslateReadyCallbacks || [];
      window.__lsGoogleTranslateReadyCallbacks = [];
      cbs.forEach((fn) => {
        try{
          fn();
        } catch (err){
          // eslint-disable-next-line no-console
          console.warn(err);
        }
      });
    };

    const addScript = document.createElement('script');
    addScript.setAttribute('data-ls-google-translate', '1');
    addScript.setAttribute('src', `//translate.google.com/translate_a/element.js?cb=${GT_SCRIPT_CB_NAME}`);
    addScript.async = true;
    document.body.appendChild(addScript);
  }

  googleTranslateElementInit(){
    const host = document.getElementById('google_translate_element');
    if(!host || host.querySelector('.goog-te-gadget')){
      this._attachComboWhenReady();
      return;
    }
    const TE = window.google && window.google.translate && window.google.translate.TranslateElement;
    if(!TE){
      return;
    }
    const layout =
      TE.InlineLayout && TE.InlineLayout.SIMPLE !== undefined
        ? TE.InlineLayout.SIMPLE
        : undefined;
    const opts = {
      pageLanguage: 'en',
      includedLanguages: INCLUDED,
      autoDisplay: false,
    };
    if(layout !== undefined){
      opts.layout = layout;
    }
    try{
      /* eslint-disable no-new */
      new TE(opts, 'google_translate_element');
    } catch (err){
      // eslint-disable-next-line no-console
      console.warn('GoogleTranslate init failed', err);
    }
    this._attachComboWhenReady();
    this._watchEngineFocusables();
  }

  _attachComboWhenReady(){
    if(this._comboPollId){
      window.clearInterval(this._comboPollId);
    }
    let tries = 0;
    const max = 200;
    this._comboPollId = window.setInterval(() => {
      tries += 1;
      const combo = findTranslateCombo();
      if(combo){
        window.clearInterval(this._comboPollId);
        this._comboPollId = null;
        this._usesMenuIframe = false;
        this._wireCombo(combo);
        return;
      }
      const gadget = document.querySelector('#google_translate_element .goog-te-gadget-simple');
      if(gadget){
        window.clearInterval(this._comboPollId);
        this._comboPollId = null;
        this._usesMenuIframe = true;
        this._onEngineReady();
        return;
      }
      if(tries >= max){
        window.clearInterval(this._comboPollId);
        this._comboPollId = null;
      }
    }, 50);
  }

  _wireCombo(combo){
    if(this._combo === combo) return;
    if(this._combo && this._onComboChange){
      this._combo.removeEventListener('change', this._onComboChange);
    }
    this._combo = combo;
    combo.addEventListener('change', this._onComboChange);
    this._onEngineReady();
  }

  _onEngineReady(){
    const combo = this._combo;
    if(!combo && !this._usesMenuIframe) return;

    let items = [...LANGUAGE_CHOICES];
    let selectedValue = '';

    if(combo){
      const allowed = new Set();
      for(let i = 0; i < combo.options.length; i += 1){
        allowed.add(combo.options[i].value);
      }

      items = LANGUAGE_CHOICES.filter((row) => {
        if(row.value === ''){
          return allowed.has('') || allowed.has('en');
        }
        return allowed.has(row.value);
      });
      if(items.length === 0){
        items = [...LANGUAGE_CHOICES];
      }
      selectedValue = combo.value;
    }

    this.setState({
      engineReady: true,
      items,
      selectedValue,
    });
    this._detachEngineFromTabOrder();
    this._watchEngineFocusables();
  }

  _onComboChange(){
    const combo = this._combo;
    if(!combo) return;
    this.setState({ selectedValue: combo.value, menuOpen: false });
  }

  _restoreOriginalLanguage(){
    clearGoogTransCookie();
    if(clickGoogleRestoreButton()){
      this.setState({ menuOpen: false, selectedValue: '' });
      window.setTimeout(() => applyGoogleTranslateBannerOffset(), 400);
      return;
    }
    this._openGoogleMenu();
    window.setTimeout(() => {
      this._clickGoogleMenuLabel('Select Language');
      this.setState({ menuOpen: false, selectedValue: '' });
    }, 80);
  }

  _resolveGoogleMenuLabel(value){
    if(value === '' || value === 'en'){
      return null;
    }
    return VALUE_TO_GOOGLE_MENU_LABEL[value] || null;
  }

  _openGoogleMenu(){
    const trigger = findTranslateMenuTrigger();
    if(trigger){
      trigger.click();
    }
  }

  _clickGoogleMenuLabel(label, attempt = 0){
    const iframe = findTranslateMenuIframe();
    if(!iframe){
      if(attempt < 30){
        window.setTimeout(() => this._clickGoogleMenuLabel(label, attempt + 1), 50);
      }
      return;
    }
    const doc = iframe.contentDocument;
    if(!doc) return;
    const textEl = Array.from(doc.querySelectorAll('.text')).find(
      (el) => el.textContent.trim() === label,
    );
    if(!textEl) return;
    const link = textEl.closest('a');
    if(link){
      link.click();
    }
  }

  _applyLanguageViaMenu(value){
    if(value === '' || value === 'en'){
      if(isPageTranslated()){
        this._restoreOriginalLanguage();
      } else {
        this.setState({ menuOpen: false, selectedValue: '' });
      }
      return;
    }
    const label = this._resolveGoogleMenuLabel(value);
    if(!label){
      this.setState({ menuOpen: false, selectedValue: '' });
      return;
    }
    this._openGoogleMenu();
    window.setTimeout(() => {
      this._clickGoogleMenuLabel(label);
      this.setState({ menuOpen: false, selectedValue: value });
      window.setTimeout(() => applyGoogleTranslateBannerOffset(), 400);
    }, 80);
  }

  _applyLanguage(value){
    let combo = this._combo;
    if(!combo || !document.body.contains(combo)){
      combo = findTranslateCombo();
      if(combo){
        this._usesMenuIframe = false;
        this._wireCombo(combo);
      }
    }
    combo = this._combo;
    if(!combo){
      this._applyLanguageViaMenu(value);
      return;
    }
    let resolved = value;
    if(value === ''){
      if(!Array.from(combo.options).some((o) => o.value === '')){
        if(Array.from(combo.options).some((o) => o.value === 'en')){
          resolved = 'en';
        }
      }
    }
    const optExists = Array.from(combo.options).some((o) => o.value === resolved);
    if(!optExists){
      this._applyLanguageViaMenu(value);
      return;
    }
    combo.value = resolved;
    triggerHtmlChange(combo);
    if(resolved === '' || resolved === 'en'){
      triggerHtmlChange(combo);
    }
    this.setState({ menuOpen: false, selectedValue: resolved });
  }

  renderHeaderPicker(){
    const { menuOpen, items, engineReady } = this.state;
    return (
      <div className="notranslate block-gtranslate block-gtranslate--header" ref={this._rootRef}>
        <div className="gt_switcher">
          <div className="gt_selected">
            <button
              type="button"
              className="gt_selected__btn"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              aria-label="Select language"
              aria-busy={!engineReady}
              onClick={this._toggleMenu}
              onKeyDown={this._onSelectedBtnKeyDown}
            >
              <span className="visually-hidden">Select language</span>
            </button>
          </div>
          {menuOpen && items.length > 0 ? (
            <ul className="gt_option" role="listbox">
              {items.map((row) => (
                <li key={row.value || 'en'} role="none">
                  <button
                    type="button"
                    className="gt_option__row"
                    role="option"
                    aria-selected={row.value === this.state.selectedValue}
                    onClick={() => this._applyLanguage(row.value)}
                  >
                    <img className="gt_option__flag" src={flagSrc(row.flag)} alt="" width="24" height="24" loading="lazy"/>
                    <span className="gt_option__label">{row.native}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    );
  }

  renderLegacyGadget(){
    return <div className="google-translate" id="google_translate_element"></div>;
  }

  render(){
    if(this.props.variant === 'header'){
      return (
        <div className="google-translate google-translate--with-header-ui">
          {this.renderHeaderPicker()}
          <div className="google-translate__engine"
               id="google_translate_element"
               ref={this._engineRef}
               aria-hidden="true"/>
        </div>
      );
    }
    return this.renderLegacyGadget();
  }
}
